import { forwardRef, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { Button as GoButton } from "@gogoro/go-design"
import classnames from "classnames"

import Button from "components/Button"
import IconButton from "components/IconButton"
import Modal from "components/Modality/Modal"

import styles from "./Panel.module.scss"

type PanelProps = {
  className?: string
  style?: React.CSSProperties
  title?: string
  children: React.ReactNode
  onOpen?: () => void
  onClose?: () => void
  onChange?: () => void
  onInput?: () => void
  onSubmit?: (val: any) => void
  withCloseButton?: any
  withCloseAction?: any
  disabled?: boolean
  submitLabel?: string
  cancelLabel?: string
  withSecondaryAction?: any
  secondaryActionLabel?: string
  testId?: string
}

const Panel = (
  {
    className,
    style,
    title,
    children,
    onOpen,
    onClose,
    onChange,
    onInput,
    onSubmit,
    withCloseButton = true,
    withCloseAction,
    disabled,
    submitLabel,
    cancelLabel,
    withSecondaryAction = false,
    secondaryActionLabel,
    testId,
  }: PanelProps,
  ref: any,
) => {
  const { t } = useTranslation("label")

  const withForm = typeof onSubmit === "function"

  const closePanel = useCallback(() => ref.current.close(), [ref])

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (onSubmit) {
        onSubmit(event)
      }
    },
    [onSubmit],
  )

  return (
    <Modal
      ref={ref}
      className={classnames(styles.container, className)}
      style={style}
      onOpen={onOpen}
      onClose={onClose}
      isDialog={false}>
      <article data-testid={testId}>
        <header className={styles.header}>
          <h2>{title}</h2>
          {withCloseButton ? <IconButton variant="bare" icon="x" onClick={closePanel} title={t("close")} /> : null}
        </header>
        <div className={styles.content}>
          {!withCloseAction && withForm ? (
            <form onChange={onChange} onInput={onInput} onSubmit={handleSubmit} data-testid={`${testId}-form`}>
              {children}
              {withSecondaryAction ? (
                <p className={styles.action}>
                  <GoButton fontOnly type="reset" onClick={closePanel}>
                    {cancelLabel || t("cancel")}
                  </GoButton>
                  <GoButton outline type="button" onClick={withSecondaryAction}>
                    {secondaryActionLabel}
                  </GoButton>
                  <GoButton type="submit" disabled={disabled}>
                    {submitLabel || t("submit")}
                  </GoButton>
                </p>
              ) : (
                <p className={styles.action}>
                  <Button variant="secondary" type="reset" onClick={closePanel}>
                    {cancelLabel || t("cancel")}
                  </Button>
                  <Button variant="primary" type="submit" disabled={disabled}>
                    {submitLabel || t("submit")}
                  </Button>
                </p>
              )}
            </form>
          ) : (
            children
          )}
        </div>
        {withCloseAction ? (
          <footer className={styles.action}>
            <Button variant="primary" onClick={closePanel}>
              {t("close")}
            </Button>
          </footer>
        ) : null}
      </article>
    </Modal>
  )
}

export default forwardRef(Panel)
